import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  closestCenter,
  pointerWithin,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ScheduledActivityResponse } from '../../types/scheduledActivity';
import type { ModalActivity } from '../../types/activity';
import {
  useScheduledActivitiesByDate,
  useDeleteScheduledActivity,
  useUpdateScheduledActivity,
  useCreateScheduledActivity,
} from '../../hooks/useScheduledActivities';
import { useModalActivities, useTransitions } from '../../hooks/useActivities';
import { DateTime } from 'luxon';
import { normalizeToMidnightUTC, formatDateNavigation, isPast } from '../../utils/dateHelpers';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { TIME_PERIOD_SECTIONS, resolveTransitionIds, groupByTimePeriod } from '../../utils/timePeriodConfig';
import { TimePeriodSection } from './TimePeriodSection';
import { ActivitySelectionList } from './ActivitySelectionList';
import { DraggingActivityCard } from './DraggingActivityCard';

const AGE_RANGE_OPTIONS = [
  { label: '2 – 3', fromAge: 2, toAge: 3 },
  { label: '3 – 4', fromAge: 3, toAge: 4 },
  { label: '4 – 5', fromAge: 4, toAge: 5 },
] as const;

function toAgeKey(fromAge: number, toAge: number): string {
  return `${fromAge}-${toAge}`;
}

interface ManageScheduledActivitiesModalProps {
  open: boolean;
  onClose: () => void;
  date: Date;
}

export function ManageScheduledActivitiesModal({
  open,
  onClose,
  date,
}: ManageScheduledActivitiesModalProps) {
  // Internal date navigation state (initialised from the parent's date prop)
  const [currentDate, setCurrentDate] = useState<Date>(date);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Age range filter state
  const [selectedAgeRanges, setSelectedAgeRanges] = useState<Set<string>>(new Set());

  // Data fetching
  const dateString = normalizeToMidnightUTC(currentDate);
  const {
    data: scheduledActivities,
    isLoading: isLoadingScheduled,
  } = useScheduledActivitiesByDate(dateString, { enabled: open });

  const { data: transitionsData } = useTransitions();
  const transitions = useMemo(
    () => transitionsData?.data ?? [],
    [transitionsData?.data]
  );

  // Fetch lightweight activity list for the "Add Activities" panel
  const { data: modalActivities } = useModalActivities();

  // Delete state
  const [activityToDelete, setActivityToDelete] = useState<ScheduledActivityResponse | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const deleteMutation = useDeleteScheduledActivity();

  // Date navigation helpers
  const handlePrevDay = () => {
    setCurrentDate(prev => {
      const dt = DateTime.fromJSDate(prev, { zone: 'Europe/London' });
      return dt.minus({ days: 1 }).toJSDate();
    });
  };

  const handleNextDay = () => {
    setCurrentDate(prev => {
      const dt = DateTime.fromJSDate(prev, { zone: 'Europe/London' });
      return dt.plus({ days: 1 }).toJSDate();
    });
  };

  // Disable the < button when the previous day would be in the past
  const prevDayIsPast = isPast(
    DateTime.fromJSDate(currentDate, { zone: 'Europe/London' }).minus({ days: 1 }).toJSDate()
  );

  // Grouping logic
  const sectionToTransitionId = useMemo(
    () => resolveTransitionIds(transitions),
    [transitions]
  );

  const groupedActivities = useMemo(
    () => groupByTimePeriod(scheduledActivities ?? [], sectionToTransitionId),
    [scheduledActivities, sectionToTransitionId]
  );

  // DnD setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // 5px drag threshold
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Activity list items use pointerWithin so a section only activates when the
  // pointer is physically inside its bounds. Scheduled items keep closestCenter
  // for smooth within/cross-section reordering previews.
  const collisionDetection = useCallback(
    (args: Parameters<typeof closestCenter>[0]) => {
      if ((args.active.id as string).startsWith('activity-')) {
        return pointerWithin(args);
      }
      return closestCenter(args);
    },
    []
  );

  // Local state for drag operations
  const [localGroups, setLocalGroups] = useState<Map<string, ScheduledActivityResponse[]>>(
    new Map()
  );
  const [activeItem, setActiveItem] = useState<ScheduledActivityResponse | null>(null);
  const [activeListActivity, setActiveListActivity] = useState<ModalActivity | null>(null);
  const [isDragSaving, setIsDragSaving] = useState(false);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [invalidDropTargets, setInvalidDropTargets] = useState<Set<string>>(new Set());
  const updateMutation = useUpdateScheduledActivity();
  const createMutation = useCreateScheduledActivity();
  const dragOriginContainer = useRef<string | null>(null);

  // Sync local state when server data changes
  useEffect(() => {
    if (!isDragSaving) {
      setLocalGroups(groupedActivities);
    }
  }, [groupedActivities, isDragSaving]);

  // Age range filter helpers
  const isViewOnly = selectedAgeRanges.size > 1;

  const toggleAgeRange = (key: string) => {
    setSelectedAgeRanges(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Display groups: localGroups filtered by selected age ranges (render-only; DnD uses localGroups)
  const displayGroups = useMemo(() => {
    if (selectedAgeRanges.size === 0) return localGroups;

    const result = new Map<string, ScheduledActivityResponse[]>();
    for (const [key, activities] of localGroups) {
      let filtered = activities.filter(sa =>
        sa.activity.ageRanges.some(ar =>
          selectedAgeRanges.has(toAgeKey(ar.fromAge, ar.toAge))
        )
      );

      if (isViewOnly) {
        filtered = [...filtered].sort((a, b) => {
          const aMin = a.activity.ageRanges.length > 0
            ? Math.min(...a.activity.ageRanges.map(ar => ar.fromAge))
            : Infinity;
          const bMin = b.activity.ageRanges.length > 0
            ? Math.min(...b.activity.ageRanges.map(ar => ar.fromAge))
            : Infinity;
          return aMin - bMin;
        });
      }

      result.set(key, filtered);
    }
    return result;
  }, [localGroups, selectedAgeRanges, isViewOnly]);

  // Helper: Find container for item
  const findContainerForItem = (
    itemId: string,
    groups: Map<string, ScheduledActivityResponse[]>
  ): string | null => {
    const scheduledId = itemId.replace('scheduled-', '');
    for (const [sectionKey, items] of groups) {
      if (items.some(a => a.id === scheduledId)) {
        return sectionKey;
      }
    }
    return null;
  };

  const findContainerForDroppable = (
    droppableId: string,
    groups: Map<string, ScheduledActivityResponse[]>
  ): string | null => {
    // Direct section drop target
    if (droppableId.startsWith('section-')) {
      return droppableId.replace('section-', '');
    }
    // Item in a section
    return findContainerForItem(droppableId, groups);
  };

  // Check if an activity already exists in a target section (per-period duplicate check)
  // excludeScheduledId: skip this scheduled activity (the one being moved) so it doesn't count as its own duplicate
  const isDuplicateInSection = (activityId: string, targetSectionKey: string, excludeScheduledId?: string): boolean => {
    const sectionItems = localGroups.get(targetSectionKey) ?? [];
    return sectionItems.some(sa => sa.activityId === activityId && sa.id !== excludeScheduledId);
  };

  // Move validation: checks whole_day boundary + per-period duplicate
  const isValidMove = (sourceSectionKey: string, targetSectionKey: string, activityId: string, excludeScheduledId?: string): boolean => {
    // Cannot move between Whole Day and time period sections
    // if (sourceSectionKey === 'whole_day' && targetSectionKey !== 'whole_day') return false;
    // if (sourceSectionKey !== 'whole_day' && targetSectionKey === 'whole_day') return false;

    // // Same section is always valid (reorder)
    if (sourceSectionKey === targetSectionKey) return true;

    // Per-period duplicate check: same activity cannot exist twice in the same column
    if (isDuplicateInSection(activityId, targetSectionKey, excludeScheduledId)) return false;

    return true;
  };

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const dragId = active.id as string;

    // Drag from activity list
    if (dragId.startsWith('activity-')) {
      const activityId = dragId.replace('activity-', '');
      const activity = modalActivities?.find(a => a.id === activityId);
      if (activity) {
        setActiveListActivity(activity);
      }

      // Compute invalid targets: sections that already contain this activity
      const targets = new Set<string>();
      for (const [sectionKey, items] of localGroups) {
        if (items.some(sa => sa.activityId === activityId)) {
          targets.add(sectionKey);
        }
      }
      setInvalidDropTargets(targets);
      return;
    }

    // Existing: drag of a scheduled item
    const scheduledId = dragId.replace('scheduled-', '');
    dragOriginContainer.current = findContainerForItem(dragId, localGroups);

    let draggedActivityId = '';
    for (const [, items] of localGroups) {
      const item = items.find(a => a.id === scheduledId);
      if (item) {
        setActiveItem(item);
        draggedActivityId = item.activityId;
        break;
      }
    }

    // Compute invalid targets for scheduled item moves
    const sourceSection = dragOriginContainer.current;
    if (sourceSection && draggedActivityId) {
      const targets = new Set<string>();
      for (const section of TIME_PERIOD_SECTIONS) {
        if (section.key === sourceSection) continue; // Source section is valid (reorder)
        if (!isValidMove(sourceSection, section.key, draggedActivityId)) {
          targets.add(section.key);
        }
      }
      setInvalidDropTargets(targets);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      setHoveredSection(null);
      return;
    }

    // Track which section is being hovered (for visual feedback)
    const overContainer = findContainerForDroppable(over.id as string, localGroups);
    setHoveredSection(overContainer);

    // For activity-list items, no local state changes needed
    const dragId = active.id as string;
    if (dragId.startsWith('activity-')) return;

    // Existing: cross-container move logic for scheduled items
    const activeContainer = findContainerForItem(dragId, localGroups);

    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    // Look up the activityId for the dragged scheduled activity
    const scheduledIdForLookup = dragId.replace('scheduled-', '');
    const draggedItem = localGroups.get(activeContainer)?.find(a => a.id === scheduledIdForLookup);
    if (!draggedItem) return;

    // Validate move
    if (!isValidMove(activeContainer, overContainer, draggedItem.activityId)) return;

    // Move item between containers in local state
    setLocalGroups(prev => {
      const next = new Map(prev);
      const scheduledId = dragId.replace('scheduled-', '');

      const sourceItems = [...(next.get(activeContainer) ?? [])];
      const targetItems = [...(next.get(overContainer) ?? [])];

      const itemIndex = sourceItems.findIndex(a => a.id === scheduledId);
      if (itemIndex === -1) return prev;

      const [movedItem] = sourceItems.splice(itemIndex, 1);
      targetItems.push(movedItem); // Add to end of target

      next.set(activeContainer, sourceItems);
      next.set(overContainer, targetItems);

      return next;
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    console.log('[DragEnd] === START ===');
    console.log('[DragEnd] active.id:', active.id, 'over:', over?.id ?? 'NULL');
    setActiveItem(null);
    setActiveListActivity(null);
    setHoveredSection(null);
    setInvalidDropTargets(new Set());

    if (!over) { console.log('[DragEnd] EARLY RETURN: no over'); return; }

    const dragId = active.id as string;
    console.log('[DragEnd] dragId:', dragId);

    // ── Handle drag from activity list (create new scheduled activity) ──
    if (dragId.startsWith('activity-')) {
      console.log('[DragEnd] Activity list drag path');
      const activityId = dragId.replace('activity-', '');
      const targetSectionKey = findContainerForDroppable(over.id as string, localGroups);
      console.log('[DragEnd] targetSectionKey:', targetSectionKey);
      if (!targetSectionKey) { console.log('[DragEnd] EARLY RETURN: no targetSectionKey'); return; }

      // Per-period duplicate check
      if (isDuplicateInSection(activityId, targetSectionKey)) {
        const sectionConfig = TIME_PERIOD_SECTIONS.find(s => s.key === targetSectionKey);
        console.log('[DragEnd] EARLY RETURN: duplicate in section');
        toast.error(`Activity already in ${sectionConfig?.label ?? targetSectionKey}`);
        return;
      }

      // Get transition ID for target section
      const targetTransitionId = sectionToTransitionId.get(targetSectionKey);
      const transitionIdNumber = targetTransitionId ? parseInt(targetTransitionId, 10) : undefined;

      // Calculate position (append to end of target column)
      const targetItems = localGroups.get(targetSectionKey) ?? [];
      const newPosition = targetItems.length + 1;

      // Get activity data for toast message
      const activity = modalActivities?.find(a => a.id === activityId);
      const sectionConfig = TIME_PERIOD_SECTIONS.find(s => s.key === targetSectionKey);

      console.log('[DragEnd] Creating scheduled activity:', { activityId, dateString, newPosition, transitionIdNumber });
      setIsDragSaving(true);
      try {
        await createMutation.mutateAsync({
          activityId: parseInt(activityId, 10),
          scheduledDate: dateString,
          position: newPosition,
          activityTransitionId: transitionIdNumber,
        });
        console.log('[DragEnd] Create SUCCESS');
        toast.success(`${activity?.title ?? 'Activity'} scheduled in ${sectionConfig?.label ?? targetSectionKey}`);
      } catch (err) {
        console.error('[DragEnd] Create FAILED:', err);
        toast.error('Could not schedule activity');
      } finally {
        setIsDragSaving(false);
      }
      return;
    }

    // ── Handle scheduled item drag (reorder / cross-container move) ──
    const originContainer = dragOriginContainer.current;
    dragOriginContainer.current = null;
    console.log('[DragEnd] Scheduled item drag path. originContainer:', originContainer);

    if (!originContainer) { console.log('[DragEnd] EARLY RETURN: no originContainer'); return; }

    const currentItemContainer = findContainerForItem(dragId, localGroups);
    const dropTargetContainer = findContainerForDroppable(over.id as string, localGroups);
    console.log('[DragEnd] currentItemContainer:', currentItemContainer, 'dropTargetContainer:', dropTargetContainer);

    const overContainer = (currentItemContainer && currentItemContainer !== originContainer)
      ? currentItemContainer
      : (dropTargetContainer ?? currentItemContainer);
    console.log('[DragEnd] resolved overContainer:', overContainer);
    if (!overContainer) { console.log('[DragEnd] EARLY RETURN: no overContainer'); return; }

    const scheduledId = dragId.replace('scheduled-', '');
    const isCrossContainerMove = originContainer !== overContainer;
    console.log('[DragEnd] scheduledId:', scheduledId, 'isCrossContainerMove:', isCrossContainerMove);

    if (!isCrossContainerMove) {
      // Reorder within same container
      const items = localGroups.get(overContainer) ?? [];
      const oldIndex = items.findIndex(a => a.id === scheduledId);
      const overScheduledId = (over.id as string).replace('scheduled-', '');
      const newIndex = items.findIndex(a => a.id === overScheduledId);
      console.log('[DragEnd] Reorder: oldIndex:', oldIndex, 'newIndex:', newIndex, 'overScheduledId:', overScheduledId);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        console.log('[DragEnd] EARLY RETURN: reorder indices invalid or same');
        return;
      }

      // Update local state immediately
      setLocalGroups(prev => {
        const next = new Map(prev);
        const reordered = arrayMove([...(next.get(overContainer) ?? [])], oldIndex, newIndex);
        next.set(overContainer, reordered);
        return next;
      });

      // Save to backend
      const newPosition = newIndex + 1; // 1-based
      console.log('[DragEnd] Reorder API call: id:', scheduledId, 'position:', newPosition);
      setIsDragSaving(true);
      try {
        await updateMutation.mutateAsync({
          id: scheduledId,
          dto: { position: newPosition },
        });
        console.log('[DragEnd] Reorder SUCCESS');
        toast.success('Order updated');
      } catch (err) {
        console.error('[DragEnd] Reorder FAILED:', err);
        toast.error('Could not update order');
      } finally {
        setIsDragSaving(false);
      }
    } else {
      // Cross-container move
      const draggedItem =
        localGroups.get(overContainer)?.find(a => a.id === scheduledId) ??
        localGroups.get(originContainer)?.find(a => a.id === scheduledId);
      console.log('[DragEnd] Cross-container: draggedItem:', draggedItem?.id, 'activityId:', draggedItem?.activityId);

      if (!draggedItem) { console.log('[DragEnd] EARLY RETURN: no draggedItem'); return; }
      const moveValid = isValidMove(originContainer, overContainer, draggedItem.activityId, scheduledId);
      console.log('[DragEnd] isValidMove:', moveValid);
      if (!moveValid) { console.log('[DragEnd] EARLY RETURN: invalid move'); return; }

      // If handleDragOver didn't move the item, update local state now
      const itemAlreadyMoved = currentItemContainer !== originContainer;
      console.log('[DragEnd] itemAlreadyMoved:', itemAlreadyMoved);
      if (!itemAlreadyMoved) {
        setLocalGroups(prev => {
          const next = new Map(prev);
          const sourceItems = [...(next.get(originContainer) ?? [])];
          const targetItems = [...(next.get(overContainer) ?? [])];
          const itemIndex = sourceItems.findIndex(a => a.id === scheduledId);
          if (itemIndex === -1) return prev;
          const [movedItem] = sourceItems.splice(itemIndex, 1);
          targetItems.push(movedItem);
          next.set(originContainer, sourceItems);
          next.set(overContainer, targetItems);
          return next;
        });
      }

      // Calculate position: use current index if already in target, else append to end
      const targetItems = localGroups.get(overContainer) ?? [];
      const posInTarget = targetItems.findIndex(a => a.id === scheduledId);
      const newPosition = posInTarget !== -1 ? posInTarget + 1 : targetItems.length + 1;

      // Get the transition ID for the target section
      const targetTransitionId = sectionToTransitionId.get(overContainer);
      const transitionIdNumber = targetTransitionId ? parseInt(targetTransitionId, 10) : null;

      console.log('[DragEnd] Cross-container API call:', { scheduledId, overContainer, newPosition, targetTransitionId, transitionIdNumber });
      setIsDragSaving(true);
      try {
        await updateMutation.mutateAsync({
          id: scheduledId,
          dto: {
            activityTransitionId: transitionIdNumber,
            position: newPosition,
          },
        });
        console.log('[DragEnd] Cross-container move SUCCESS');
        toast.success('Activity moved');
      } catch (err) {
        console.error('[DragEnd] Cross-container move FAILED:', err);
        toast.error('Could not move activity');
      } finally {
        setIsDragSaving(false);
      }
    }
    console.log('[DragEnd] === END ===');
  };

  // Delete handlers
  const handleDeleteRequest = (sa: ScheduledActivityResponse) => {
    setActivityToDelete(sa);
  };

  const handleDeleteConfirm = async () => {
    if (!activityToDelete) return;
    const id = activityToDelete.id;
    setDeletingIds(prev => new Set(prev).add(id));
    setActivityToDelete(null);

    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Activity removed from schedule');
    } catch {
      toast.error('Could not remove activity');
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <>
      {/* Main Modal */}
      <Dialog open={open} onOpenChange={onClose} >
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto bg-white border-2 border-gray-300 shadow-2xl">
          <DialogHeader className="bg-gradient-to-r from-blue-50 to-blue-100 -m-6 mb-6 p-6 border-b-2 border-blue-200">
            <DialogTitle className="text-1xl ml-4 font-bold text-gray-900">
              Schedule and organize activities by time period
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              <div className="flex flex-row gap-4 mt-2">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={handlePrevDay}
                    disabled={prevDayIsPast}
                    aria-label="Previous day"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        className="text-2xl font-bold text-gray-900 underline decoration-dotted underline-offset-4 cursor-pointer hover:text-blue-600 transition-colors"
                        type="button"
                      >
                        {formatDateNavigation(currentDate)}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={currentDate}
                        onSelect={(day) => {
                          if (day) {
                            setCurrentDate(day);
                            setIsDatePickerOpen(false);
                          }
                        }}
                        disabled={(day) => isPast(day)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={handleNextDay}
                    aria-label="Next day"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                {/* Age range filter pills */}
                <div className="flex items-center gap-2 flex-wrap">
                  {AGE_RANGE_OPTIONS.map(option => {
                    const key = toAgeKey(option.fromAge, option.toAge);
                    const isSelected = selectedAgeRanges.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleAgeRange(key)}
                        className={`px-3 py-1 rounded-full text-sm border transition-colors ${isSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}

                  {selectedAgeRanges.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedAgeRanges(new Set())}
                      className="text-sm text-gray-400 hover:text-gray-600 ml-1"
                    >
                      Clear
                    </button>
                  )}

                  {isViewOnly && (
                    <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded ml-2">
                      View only — editing disabled
                    </span>
                  )}
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>

          {/* Time Period Sections + Activity List (shared DndContext) */}
          {isLoadingScheduled ? (
            <div className="bg-gray-50 -mx-6 px-6 py-4 rounded-lg">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {TIME_PERIOD_SECTIONS.map(section => (
                  <div key={section.key} className="space-y-2">
                    <Skeleton className="h-10 w-full rounded" />
                    <Skeleton className="h-24 w-full rounded" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetection}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <div className="bg-gray-50 -mx-6 px-6 py-4 rounded-lg">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                  {TIME_PERIOD_SECTIONS.map(section => (
                    <TimePeriodSection
                      key={section.key}
                      config={section}
                      activities={displayGroups.get(section.key) ?? []}
                      onDeleteActivity={handleDeleteRequest}
                      deletingIds={deletingIds}
                      isDragSaving={isDragSaving}
                      isHovered={hoveredSection === section.key}
                      isInvalidTarget={invalidDropTargets.has(section.key)}
                      viewOnly={isViewOnly}
                    />
                  ))}
                </div>
              </div>

              {/* Activity Selection List — hidden in view-only mode */}
              {!isViewOnly && (
                <div className="border-t-2 border-gray-300 pt-6 mt-6 bg-gray-50 -mx-6 px-6 py-4 rounded-lg">
                  <ActivitySelectionList
                    transitions={transitions}
                    isDragSaving={isDragSaving}
                    ageRangeFilter={
                      selectedAgeRanges.size === 1
                        ? [...selectedAgeRanges][0]
                        : undefined
                    }
                  />
                </div>
              )}

              {createPortal(
                <DragOverlay>
                  {activeItem ? (
                    <DraggingActivityCard scheduledActivity={activeItem} />
                  ) : activeListActivity ? (
                    <div className="w-48 p-2 bg-white rounded border border-gray-200 shadow-lg cursor-grabbing flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{activeListActivity.title}</p>
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>,
                document.body
              )}
            </DndContext>
          )}

          {/* Footer */}
          <DialogFooter className="border-t-2 border-gray-200 -mx-6 -mb-6 px-6 py-4 bg-gray-50">
            <Button onClick={onClose} className="min-w-[100px]">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!activityToDelete} onOpenChange={() => setActivityToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Remove Scheduled Activity
            </DialogTitle>
            <DialogDescription>
              This will remove the activity from the schedule for this date.
            </DialogDescription>
          </DialogHeader>
          {activityToDelete && (
            <div className="py-4">
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="font-medium">{activityToDelete.activity.title}</p>
              </div>
              <div className="flex items-start gap-2 p-3 mt-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  The activity itself will not be deleted and can be scheduled again.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityToDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
